import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
} from '@tanstack/react-query'
import {
    CreateInviteLinksGroupDtoForChannels,
    UpdateInviteLinksGroupDto,
} from '@synapex/shared'
import { inviteLinksGroupService } from './invite-links-group.service'
import { InviteLinksGroupChannelsType } from './invite-links-group.types'
import { LinksQueryKeyConstants } from './invite-links-group.query.constants'

export function useCreateInviteLinksGroupChannelsMutation(
    options?: UseMutationOptions<
        InviteLinksGroupChannelsType,
        Error,
        CreateInviteLinksGroupDtoForChannels
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        InviteLinksGroupChannelsType,
        Error,
        CreateInviteLinksGroupDtoForChannels
    >({
        mutationFn: (data: CreateInviteLinksGroupDtoForChannels) =>
            inviteLinksGroupService.createGroupForChannels(data),
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [
                    LinksQueryKeyConstants.GroupsChannels,
                    data.channel_id,
                ],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

interface UpdateInviteLinksGroupVariables {
    id: number
    data: UpdateInviteLinksGroupDto
}

export function useUpdateInviteLinksGroupChannelsMutation(
    options?: UseMutationOptions<
        InviteLinksGroupChannelsType,
        Error,
        UpdateInviteLinksGroupVariables
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        InviteLinksGroupChannelsType,
        Error,
        UpdateInviteLinksGroupVariables
    >({
        mutationFn: ({ id, data }: UpdateInviteLinksGroupVariables) =>
            inviteLinksGroupService.updateGroupForChannels(id, data),
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [
                    LinksQueryKeyConstants.GroupsChannels,
                    data.channel_id,
                ],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useDeleteInviteLinksGroupChannelsMutation(
    options?: UseMutationOptions<number, Error, number>
) {
    const queryClient = useQueryClient()

    return useMutation<number, Error, number>({
        mutationFn: (id: number) =>
            inviteLinksGroupService.deleteGroupForChannels(id),
        ...options,
        onSuccess: (channelId, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsChannels, channelId],
            })
            if (options?.onSuccess) {
                options.onSuccess(channelId, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}
